import React, {useEffect, useContext} from 'react';
import PropTypes from 'prop-types';
import Experiment from '@homeaway/react-experiment';

import {editReservationAttribute as editReservation} from '../../../../utils/appConfigData';
import StayContext from '../../../context/StayContext';
import translate from '../../../../utils/translations';
import {
    editReservationMessagePresented,
    MODIFY_BOOKING,
    MODIFY_REQUEST,
} from '../../../../utils/EDAP/ConversationMessages';

const shouldDisplayField = (map, newAttributeKey, oldAttributeKey) => {
    return (
        map.hasOwnProperty(newAttributeKey) ||
        map.hasOwnProperty(oldAttributeKey)
    );
};

const getOrderedAttributes = (map) => {
    const orderedAttributes = [];
    orderedAttributes.push(
        shouldDisplayField(
            map,
            editReservation.NEW_BEGIN_DATE,
            editReservation.OLD_BEGIN_DATE
        )
            ? {
                  header: 'dates',
                  newValue: map.newBeginDate
                      ? `${map.newBeginDate} - ${map.newEndDate}`
                      : null,
                  oldValue: map.oldBeginDate
                      ? `${map.oldBeginDate} - ${map.oldEndDate}`
                      : null,
              }
            : null,
        shouldDisplayField(
            map,
            editReservation.NEW_CHECKIN_TIME,
            editReservation.OLD_CHECKIN_TIME
        )
            ? {
                  header: 'checkInTime',
                  newValue: map.newCheckInTime,
                  oldValue: map.oldCheckInTime,
              }
            : null,
        shouldDisplayField(
            map,
            editReservation.NEW_CHECKOUT_TIME,
            editReservation.OLD_CHECKOUT_TIME
        )
            ? {
                  header: 'checkOutTime',
                  newValue: map.newCheckOutTime,
                  oldValue: map.oldCheckOutTime,
              }
            : null,
        shouldDisplayField(
            map,
            editReservation.NEW_NUM_ADULTS,
            editReservation.OLD_NUM_ADULTS
        )
            ? {
                  header: 'guests',
                  newValue: map.newNumAdults
                      ? `${map.newNumAdults}, ${map.newNumChildren}`
                      : null,
                  oldValue: map.oldNumAdults
                      ? `${map.oldNumAdults}, ${map.oldNumChildren}`
                      : null,
              }
            : null,
        shouldDisplayField(
            map,
            editReservation.NEW_FIRST_NAME,
            editReservation.OLD_FIRST_NAME
        )
            ? {
                  header: 'name',
                  newValue: map.newFirstName
                      ? `${map.newFirstName} ${map.newLastName}`
                      : null,
                  oldValue: map.oldFirstName
                      ? `${map.oldFirstName} ${map.oldLastName}`
                      : null,
              }
            : null,
        shouldDisplayField(
            map,
            editReservation.NEW_PHONE,
            editReservation.OLD_PHONE
        )
            ? {
                  header: 'phone',
                  newValue: map.newPhone,
                  oldValue: map.oldPhone,
              }
            : null,
        shouldDisplayField(
            map,
            editReservation.NEW_MOBILE,
            editReservation.OLD_MOBILE
        )
            ? {
                  header: 'mobile',
                  newValue: map.newMobile,
                  oldValue: map.oldMobile,
              }
            : null,
        shouldDisplayField(
            map,
            editReservation.NEW_TRAVELER_EMAIL,
            editReservation.OLD_TRAVELER_EMAIL
        )
            ? {
                  header: 'email',
                  newValue: map.newTravelerEmail,
                  oldValue: map.oldTravelerEmail,
              }
            : null
    );
    return orderedAttributes.filter(Boolean);
};

const getTranslation = (key, translations, value = null) => {
    const args = [
        translations,
        'trav-dash/trav-home',
        'messages',
        `modifyBooking.travelerInbox.approvedChange.${key}`,
    ];
    if (value) {
        args.push(value);
    }
    return translate(...args);
};

const AttributeSection = ({header, newValue, oldValue}) => {
    return (
        <div className="Message__editReservationSection">
            <div className="Message__editReservationSectionHeader">
                <b>{header}</b>
            </div>
            {oldValue && (
                <div>
                    <s>{oldValue}</s>
                </div>
            )}
            {newValue && (
                <div>
                    <b>{newValue}</b>
                </div>
            )}
        </div>
    );
};

export const EditReservation = (props) => {
    const actionLocation = MODIFY_BOOKING;
    const experienceType = MODIFY_REQUEST;
    const {attributes, avatar, role, sentText, translations} = props;
    const {
        stay: {
            booking: {reservationUuid: reservationUUID},
            unit: {integratedPropertyManager, location},
        },
    } = useContext(StayContext);
    const attributeMap = {};
    attributes.forEach((attribute) => {
        attributeMap[attribute.key] = attribute.value;
    });

    // Show message when dates, guests or time is updated/changed.
    const showMessage =
        attributeMap.hasOwnProperty(editReservation.NEW_BEGIN_DATE) ||
        attributeMap.hasOwnProperty(editReservation.NEW_CHECKIN_TIME) ||
        attributeMap.hasOwnProperty(editReservation.NEW_CHECKOUT_TIME) ||
        attributeMap.hasOwnProperty(editReservation.NEW_NUM_ADULTS);

    const orderedAttributes = getOrderedAttributes(attributeMap);

    useEffect(() => {
        editReservationMessagePresented({
            actionLocation,
            experienceType,
            reservationUUID,
        });
    }, [actionLocation, experienceType, reservationUUID]);

    const renderIpmModifiedBookingMessage = () => (
        <Experiment name={'vrbo_ipm_static_messaging'} variant={'on'}>
            <div
                data-testid="message-body"
                className="Message__body Message__editReservationSection"
            >
                {getTranslation('messageIpm', translations)}
            </div>
        </Experiment>
    );

    const renderNonIpmModifiedBookingMessage = () => (
        <div
            data-testid="message-body"
            className="Message__body Message__editReservationSection"
        >
            {getTranslation('message', translations, location)}
        </div>
    );

    return (
        <div className={`Message Message--${role}`}>
            <div className="Message__avatar">
                <div
                    className="Message__avatar__image img-circle"
                    style={{backgroundImage: `url('${avatar}')`}}
                />
            </div>
            <div className="clearfix">
                <h4 className="Message__editReservationTitle">
                    {getTranslation('title', translations)}
                </h4>
                <div className="Message__time">{sentText}</div>
            </div>
            {orderedAttributes.map(({header, newValue, oldValue}) => (
                <AttributeSection
                    header={getTranslation(header, translations)}
                    key={header}
                    newValue={newValue}
                    oldValue={oldValue}
                />
            ))}
            {showMessage &&
                integratedPropertyManager &&
                renderIpmModifiedBookingMessage()}
            {showMessage &&
                !integratedPropertyManager &&
                renderNonIpmModifiedBookingMessage()}
        </div>
    );
};

EditReservation.propTypes = {
    attributes: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            value: PropTypes.string,
        })
    ),
    avatar: PropTypes.string,
    role: PropTypes.string,
    sentText: PropTypes.string,
    translations: PropTypes.shape({}),
};
